/**
 * Hook for wallet ownership verification in YaaS
 *
 * Retail challenges use message signing first. Wallets that cannot sign a
 * message may explicitly approve a nonce-bound, 1000-lamport transfer.
 */

import { FEATURE_FLAGS } from "@solomon-apps/config";
import { useTransactionSigner, useWallet, useWalletMessageSigner } from "@solomon-apps/shared";
import type {
    RetailVerificationMethod,
    RetailVerificationProof,
    RetailWalletChallengeResponse
} from "@solomon-apps/shared";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import posthog from "posthog-js";
import { useCallback, useRef, useState } from "react";

import type { EnrollmentEvent, EnrollmentProperties, VerificationObserver } from "@/services/analytics/enrollment";
import { buildVerificationTransfer, normalizeVerificationTransfer } from "@/services/yaas/verificationTransfer";
import { classifyWalletSigningError, WalletVerificationError } from "@/services/yaas/walletVerificationError";

import { useVerificationConsentStore } from "./verificationConsentStore";

export { WalletVerificationError } from "@/services/yaas/walletVerificationError";
export type { WalletVerificationErrorCode } from "@/services/yaas/walletVerificationError";

export type VerificationProof = RetailVerificationProof;

export interface VerifyArgs
    extends Pick<RetailWalletChallengeResponse, "message">,
        Partial<Omit<RetailWalletChallengeResponse, "message">> {
    analytics?: VerificationObserver;
    purpose: "retail_enrollment" | "action";
    walletAddress: string;
    /** An explicit user-selected transfer path after a signing attempt. */
    preferredMethod?: "microTransfer";
    signal?: AbortSignal;
    onTransferBroadcast?: (signature: string) => void;
}

export interface UseWalletVerificationResult {
    verify: (args: VerifyArgs) => Promise<VerificationProof>;
    isVerifying: boolean;
    verificationMethod: RetailVerificationMethod | null;
    error: string | null;
}

function journalKey(walletAddress: string, nonce: string): string {
    return `yaas-retail-verification:${walletAddress}:${nonce}`;
}

export function readVerificationJournal(args: VerifyArgs): string | null {
    if (
        !args.nonce ||
        !args.expiresAt ||
        !Number.isFinite(Date.parse(args.expiresAt)) ||
        Date.parse(args.expiresAt) <= Date.now()
    )
        return null;
    try {
        return window.sessionStorage.getItem(journalKey(args.walletAddress, args.nonce));
    } catch (error: unknown) {
        console.warn("Could not read the verification signature journal", error);
        return null;
    }
}

function assertActive(signal?: AbortSignal): void {
    if (signal?.aborted) throw new WalletVerificationError("Verification was canceled", "wallet_mismatch");
}

/** Stops waiting without treating a late provider result as a new attempt. */
async function waitForVerification<T>(operation: Promise<T>, signal?: AbortSignal, timeoutMs?: number): Promise<T> {
    assertActive(signal);
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let abort: (() => void) | undefined;
    try {
        return await Promise.race([
            operation,
            new Promise<never>((_resolve, reject) => {
                abort = (): void => reject(new WalletVerificationError("Verification was canceled", "wallet_mismatch"));
                signal?.addEventListener("abort", abort, { once: true });
                if (timeoutMs !== undefined) {
                    timeout = setTimeout(
                        () =>
                            reject(
                                new WalletVerificationError(
                                    "Your wallet did not respond to the signing request.",
                                    "signing_timeout",
                                    "signMessage"
                                )
                            ),
                        timeoutMs
                    );
                }
            })
        ]);
    } finally {
        if (timeout !== undefined) clearTimeout(timeout);
        if (abort) signal?.removeEventListener("abort", abort);
    }
}

// Canceling the page cannot dismiss a provider's transaction approval. Reuse its
// pending result if the user returns before the wallet settles the request.
const pendingTransfers = new Map<string, Promise<string>>();

export function useWalletVerification(): UseWalletVerificationResult {
    const { connected, publicKey } = useWallet();
    const { signer, ready: signerReady } = useTransactionSigner();
    const signWalletMessage = useWalletMessageSigner();
    const [isVerifying, setIsVerifying] = useState(false);
    const [verificationMethod, setVerificationMethod] = useState<RetailVerificationMethod | null>(null);
    const [error, setError] = useState<string | null>(null);
    const attemptRef = useRef(0);

    const verify = useCallback(
        async (args: VerifyArgs): Promise<VerificationProof> => {
            const attempt = ++attemptRef.current;
            const active = (): void => {
                assertActive(args.signal);
                if (attempt !== attemptRef.current)
                    throw new WalletVerificationError("Verification was superseded", "wallet_mismatch");
            };
            setIsVerifying(true);
            setError(null);
            setVerificationMethod(null);
            const startedAt = Date.now();
            let transferStarted = false;
            let sendInvoked = false;
            const emit = (event: EnrollmentEvent, properties: EnrollmentProperties = {}): void => {
                try {
                    if (args.analytics) args.analytics(event, properties);
                    else {
                        const legacyEvents = {
                            "enroll:verification_started": "yaas_verify_attempted",
                            "enroll:proof_received": "yaas_verify_succeeded",
                            "enroll:verification_failed": "yaas_verify_failed"
                        } as const;
                        if (event in legacyEvents)
                            posthog.capture(legacyEvents[event as keyof typeof legacyEvents], {
                                purpose: args.purpose,
                                ...properties
                            });
                    }
                } catch {
                    /* Analytics must not affect verification. */
                }
            };
            let method: RetailVerificationMethod = args.preferredMethod ?? "signMessage";
            try {
                active();
                if (!connected || !publicKey || publicKey.toBase58() !== args.walletAddress) {
                    throw new WalletVerificationError(
                        "The connected wallet changed. Start again with the current wallet.",
                        "wallet_mismatch"
                    );
                }
                const journaledSignature = readVerificationJournal(args);
                if (journaledSignature) {
                    setVerificationMethod("microTransfer");
                    emit("enroll:verification_resumed", {
                        proof_source: "journal",
                        verification_method: "micro_transfer"
                    });
                    return { method: "microTransfer", transactionSignature: journaledSignature };
                }
                const transferKey = args.nonce ? journalKey(args.walletAddress, args.nonce) : null;
                const existingTransfer = transferKey ? pendingTransfers.get(transferKey) : undefined;
                if (existingTransfer) {
                    sendInvoked = true;
                    transferStarted = true;
                    method = "microTransfer";
                    setVerificationMethod(method);
                    emit("enroll:verification_resumed", {
                        proof_source: "pending_operation",
                        verification_method: "micro_transfer"
                    });
                    const transactionSignature = await waitForVerification(existingTransfer, args.signal);
                    active();
                    return { method: "microTransfer", transactionSignature };
                }
                if (args.preferredMethod !== "microTransfer") {
                    setVerificationMethod("signMessage");
                    try {
                        emit("enroll:verification_started", { method: "sign_message" });
                        const messageProof = await waitForVerification(
                            Promise.resolve().then(() => {
                                active();
                                return signWalletMessage(args.walletAddress, args.message);
                            }),
                            args.signal,
                            60_000
                        );
                        active();
                        emit("enroll:proof_received", {
                            method_used: "sign_message",
                            fallback_triggered: false,
                            verify_latency_ms: Date.now() - startedAt
                        });
                        return { method: "signMessage", ...messageProof };
                    } catch (caught: unknown) {
                        active();
                        const failure =
                            caught instanceof WalletVerificationError
                                ? caught
                                : new WalletVerificationError(
                                      "Could not sign the verification message",
                                      classifyWalletSigningError(caught),
                                      "signMessage"
                                  );
                        // Enrollment explains the failure in-page before offering an explicit paid retry.
                        // Existing account actions retain their consent-first fallback ceremony.
                        if (args.purpose === "retail_enrollment" || failure.code === "wallet_rejected") throw failure;
                    }
                }
                method = "microTransfer";
                setVerificationMethod(method);
                // Message signing uses its own wallet feature; only a transfer needs this signer.
                if (!signerReady || !signer) {
                    throw new WalletVerificationError(
                        "Wallet transaction signer is not ready. Reconnect your wallet and try again.",
                        "transfer_unavailable",
                        method
                    );
                }
                const transfer = normalizeVerificationTransfer(args.microTransfer, args.nonce);
                if (!FEATURE_FLAGS.retailMicroTransferVerification || !args.nonce || !args.expiresAt || !transfer) {
                    throw new WalletVerificationError(
                        "Transfer verification is unavailable.",
                        "transfer_unavailable",
                        method
                    );
                }
                const expiresAt = Date.parse(args.expiresAt);
                const nonce = args.nonce;
                const assertUnexpired = (): void => {
                    active();
                    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
                        throw new WalletVerificationError(
                            "The verification request expired. Start a new verification.",
                            "nonce_expired",
                            method
                        );
                    }
                };
                assertUnexpired();
                // Enrollment goes straight to the wallet: choosing (or being
                // moved on to) the transfer is the ask, and the wallet's own
                // approval prompt is the consent, so no modal stands between.
                // Account actions keep their consent-first ceremony.
                if (args.purpose !== "retail_enrollment") {
                    emit("enroll:verification_consent_viewed", { verification_method: "micro_transfer" });
                    const approved = await useVerificationConsentStore.getState().ask(transfer, args.signal);
                    emit(approved ? "enroll:verification_consent_accepted" : "enroll:verification_consent_rejected", {
                        reason: args.signal?.aborted ? "observation_aborted" : undefined
                    });
                    active();
                    if (!approved)
                        throw new WalletVerificationError(
                            "You canceled the verification transfer.",
                            "wallet_rejected",
                            method
                        );
                    assertUnexpired();
                }
                emit("enroll:verification_started", { method: "micro_transfer" });
                transferStarted = true;
                emit("enroll:transfer_started", { verification_method: "micro_transfer" });
                const latestBlockhash = await waitForVerification(
                    ConnectionManager.getRpcClient().getLatestBlockhash(),
                    args.signal
                );
                assertUnexpired();
                if (!latestBlockhash) throw new Error("Could not get a recent Solana blockhash");
                const transaction = buildVerificationTransfer({
                    payer: publicKey,
                    blockhash: latestBlockhash.blockhash,
                    transfer
                });
                // Journal even a late broadcast result: canceling the page cannot cancel a wallet broadcast.
                sendInvoked = true;
                const broadcast = signer.signAndSendTransaction(transaction.serialize(false)).then((signature) => {
                    emit("enroll:transfer_submitted", { verification_method: "micro_transfer" });
                    try {
                        window.sessionStorage.setItem(journalKey(args.walletAddress, nonce), signature);
                    } catch (error: unknown) {
                        // The enrollment callback still retains the paid proof in memory.
                        console.warn("Could not persist the verification signature journal", error);
                    }
                    args.onTransferBroadcast?.(signature);
                    if (args.analytics)
                        emit("enroll:proof_received", {
                            method_used: "micro_transfer",
                            verification_method: "micro_transfer",
                            proof_source: "fresh",
                            fallback_triggered: true,
                            verify_latency_ms: Date.now() - startedAt
                        });
                    return signature;
                });
                const key = journalKey(args.walletAddress, nonce);
                pendingTransfers.set(key, broadcast);
                void broadcast.then(
                    () => pendingTransfers.delete(key),
                    () => pendingTransfers.delete(key)
                );
                const transactionSignature = await waitForVerification(broadcast, args.signal);
                active();
                if (!args.analytics)
                    emit("enroll:proof_received", {
                        method_used: "micro_transfer",
                        fallback_triggered: true,
                        verify_latency_ms: Date.now() - startedAt
                    });
                return { method: "microTransfer", transactionSignature };
            } catch (caught: unknown) {
                const failure =
                    caught instanceof WalletVerificationError
                        ? caught
                        : new WalletVerificationError(
                              caught instanceof Error ? caught.message : "Verification failed",
                              classifyWalletSigningError(caught) === "wallet_rejected"
                                  ? "wallet_rejected"
                                  : "transfer_failed",
                              method
                          );
                if (attempt === attemptRef.current) setError(failure.message);
                const uncertain = sendInvoked && failure.code !== "wallet_rejected";
                if (transferStarted)
                    emit(uncertain ? "enroll:transfer_confirmation_deferred" : "enroll:transfer_failed", {
                        error_code: failure.code,
                        verification_method: "micro_transfer"
                    });
                if (!args.analytics || (!uncertain && !args.signal?.aborted))
                    emit("enroll:verification_failed", {
                        method_attempted: args.analytics
                            ? method === "microTransfer"
                                ? "micro_transfer"
                                : "sign_message"
                            : method,
                        error_code: failure.code
                    });
                throw failure;
            } finally {
                if (attempt === attemptRef.current) setIsVerifying(false);
            }
        },
        [connected, publicKey, signer, signerReady, signWalletMessage]
    );

    return { verify, isVerifying, verificationMethod, error };
}
